import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { KeyValuePair, Offer, OrderItem } from 'src/app/stores/stores';

export interface OfferListCardAddToCartEvent {
  offerId: number;
  categoryId: number;
  inStock: boolean;
  isDirectlyOrderable: boolean;
  isExpandable: boolean;
  isOrderable: boolean;
  basketEnabled: boolean;
  orderItem?: OrderItem;
}

export interface OfferListCardProductDetailsEvent {
  offerId: number;
  categoryId: number;
  inStock: boolean;
  isExpandable: boolean;
  isOrderable: boolean;
  basketEnabled: boolean;
}

const LONG_PRESS_MS = 500;
const TOUCH_MOVE_PX = 10;

@Component({
  selector: 'app-offer-list-card',
  templateUrl: './offer-list-card.component.html',
  styleUrls: ['./offer-list-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OfferListCardComponent {
  // ── required inputs ─────────────────────────────────────────────────────────
  readonly offer = input<Offer | null>(null);
  readonly cartItems = input.required<OrderItem[]>();
  readonly currentCartStatus = input.required<string | null>();
  readonly categoryId = input.required<number>();
  readonly locale = input.required<string>();
  readonly currency = input.required<string>();
  readonly currencySymbol = input.required<string>();

  // ── optional inputs ─────────────────────────────────────────────────────────
  /**
   * Visual layout. `'list'` is the original single-row card (image tile on the trailing edge);
   * `'tiles'` renders a catalog-style tile (image on top, name / description / price below) so
   * the recommendation page can mirror the storefront catalog's tiles layout. Both variants
   * share the same inputs / outputs / interaction model — only the arrangement differs.
   */
  readonly layout = input<'list' | 'tiles'>('list');
  readonly basketEnabled = input<boolean>(true);
  readonly orderingEnabled = input<boolean>(true);
  readonly searchTerm = input<string | null>(null);
  readonly isAdminOrderCaptureUpdate = input<boolean>(false);
  readonly orderCaptureEnabled = input<boolean>(true);

  // ── outputs ──────────────────────────────────────────────────────────────────
  readonly addToCart = output<OfferListCardAddToCartEvent>();
  readonly decrease = output<OrderItem>();
  readonly productDetailsViewed = output<OfferListCardProductDetailsEvent>();

  // Internal EventEmitters threaded into app-offer-actions (that component still uses the
  // EventEmitter-as-input pattern). We own them, subscribe once, and re-emit via our outputs
  // so the parent only deals with standard Angular (event) bindings.
  readonly _itemAddedToCart = new EventEmitter<OfferListCardAddToCartEvent>();
  readonly _decreaseItemQty = new EventEmitter<OrderItem>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly sanitizer = inject(DomSanitizer);

  // ── interaction state ────────────────────────────────────────────────────────
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private isLongPress = false;
  private touchStartX = 0;
  private touchStartY = 0;
  private isTouchMove = false;

  constructor() {
    this._itemAddedToCart
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((payload) => this.addToCart.emit(payload));

    this._decreaseItemQty
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((item) => this.decrease.emit(item));
  }

  // ── image ─────────────────────────────────────────────────────────────────────

  getBackgroundImage(url: any) {
    return url ? this.sanitizer.bypassSecurityTrustStyle(`url('${url}')`) : '';
  }

  // ── allergens / calories ─────────────────────────────────────────────────────

  getCalories(attributes: KeyValuePair[] | undefined): string | false {
    // KeyValuePair.value is typed boolean but CALORIES carries a numeric string at runtime
    const attr = attributes?.find((a) => a.key === 'CALORIES');
    return attr ? String(attr.value) : false;
  }

  // ── quantity helpers ──────────────────────────────────────────────────────────

  getTotalQty(): number {
    return this.cartItems()?.reduce((acc, item) => acc + item.quantity, 0) ?? 0;
  }

  // ── click handlers ────────────────────────────────────────────────────────────

  onRowClick(): void {
    if (this.isLongPress) {
      this.isLongPress = false;
      return;
    }
    if (this.isTouchMove) {
      this.isTouchMove = false;
      return;
    }
    if (this.currentCartStatus() !== 'LOADED') {
      return;
    }
    const offer = this.offer();
    if (!offer) {
      return;
    }
    this.handleAction(
      offer.offerId,
      offer.inStock,
      offer.isDirectlyOrderable,
      offer.isExpandable,
      offer.isOrderable,
    );
  }

  onImageClick(event: MouseEvent): void {
    // When items are already in the cart, offer-actions handles quantity changes — defer to it.
    if (this.cartItems().length > 0 || this.getTotalQty() > 0) {
      return;
    }
    if (this.isTouchMove) {
      this.isTouchMove = false;
      return;
    }
    event.stopPropagation();
    const offer = this.offer();
    if (!offer) {
      return;
    }
    this.handleAction(
      offer.offerId,
      offer.inStock,
      offer.isDirectlyOrderable,
      offer.isExpandable,
      offer.isOrderable,
    );
  }

  onInfoIconClick(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    const offer = this.offer();
    if (!offer) {
      return;
    }
    this.emitProductDetails(
      offer.offerId,
      offer.inStock,
      offer.isExpandable,
      offer.isOrderable,
    );
  }

  onOfferActionsProductDetailsViewed(
    event: OfferListCardProductDetailsEvent,
  ): void {
    this.productDetailsViewed.emit(event);
  }

  // ── long-press detection ──────────────────────────────────────────────────────

  onMouseDown(): void {
    this.isLongPress = false;
    this.longPressTimer = setTimeout(() => {
      this.isLongPress = true;
      this.fireLongPress();
    }, LONG_PRESS_MS);
  }

  onMouseUp(): void {
    this.clearTimer();
  }

  onMouseLeave(): void {
    this.clearTimer();
    this.isLongPress = false;
  }

  onTouchStart(event: TouchEvent): void {
    this.isLongPress = false;
    this.longPressTimer = setTimeout(() => {
      this.isLongPress = true;
      this.fireLongPress();
    }, LONG_PRESS_MS);

    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.isTouchMove = false;

    const moveHandler = (e: TouchEvent) => {
      const t = e.touches[0];
      if (
        Math.abs(t.clientX - this.touchStartX) > TOUCH_MOVE_PX ||
        Math.abs(t.clientY - this.touchStartY) > TOUCH_MOVE_PX
      ) {
        this.isTouchMove = true;
        this.clearTimer();
      }
    };
    document.addEventListener('touchmove', moveHandler, { passive: true });
    (event.target as any)._tmh = moveHandler;
  }

  onTouchEnd(event: TouchEvent): void {
    this.removeTouchMoveHandler(event);
    this.clearTimer();
    if (this.isTouchMove) {
      event.preventDefault();
      event.stopPropagation();
      this.isTouchMove = false;
    }
  }

  onTouchCancel(event: TouchEvent): void {
    this.removeTouchMoveHandler(event);
    this.clearTimer();
    this.isLongPress = false;
    this.isTouchMove = false;
  }

  // ── private ───────────────────────────────────────────────────────────────────

  private handleAction(
    offerId: number,
    inStock: boolean,
    isDirectlyOrderable: boolean,
    isExpandable: boolean,
    isOrderable: boolean,
  ): void {
    const categoryId = this.categoryId();
    const basketEnabled = this.basketEnabled();

    if (this.isAdminOrderCaptureUpdate()) {
      if (this.orderCaptureEnabled()) {
        this.addToCart.emit({
          offerId,
          categoryId,
          inStock,
          isDirectlyOrderable,
          isExpandable,
          isOrderable,
          basketEnabled,
        });
      } else {
        this.emitProductDetails(offerId, inStock, isExpandable, isOrderable);
      }
    } else {
      this.emitProductDetails(offerId, inStock, isExpandable, isOrderable);
    }
  }

  private fireLongPress(): void {
    const offer = this.offer();
    if (!offer) {
      return;
    }
    this.emitProductDetails(
      offer.offerId,
      offer.inStock,
      offer.isExpandable,
      offer.isOrderable,
    );
  }

  private emitProductDetails(
    offerId: number,
    inStock: boolean,
    isExpandable: boolean,
    isOrderable: boolean,
  ): void {
    this.productDetailsViewed.emit({
      offerId,
      categoryId: this.categoryId(),
      inStock,
      isExpandable,
      isOrderable,
      basketEnabled: this.basketEnabled(),
    });
  }

  private clearTimer(): void {
    if (this.longPressTimer !== null) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  private removeTouchMoveHandler(event: TouchEvent): void {
    const handler = (event.target as any)._tmh;
    if (handler) {
      document.removeEventListener('touchmove', handler);
      delete (event.target as any)._tmh;
    }
  }
}
